import React, { ChangeEvent, forwardRef, MouseEvent, useCallback, useEffect, useState } from 'react';
import { Text } from 'components/core/text';
import { Camera, FileText, Trash2 } from 'react-feather';

interface UploadBlockProps {
  name: string;
  label: string;
  onChange?: (file?: File) => void;
  removeImage: () => void;
  error: string;
  className?: string;
  defaultImage?: string; // New prop for default image
}

const isImageFile = (file: File) => {
  return file.type.startsWith('image/');
};

const UploadTag = forwardRef<HTMLInputElement, UploadBlockProps>(
  ({ name, label, removeImage, error, className, defaultImage, onChange = () => {} }, ref) => {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(defaultImage || null);
    const [localError, setLocalError] = useState<boolean>(false);
    const [isDefaultImage, setIsDefaultImage] = useState<boolean>(!!defaultImage);

    useEffect(() => {
      if (error != '' && !file && !isDefaultImage) {
        setLocalError(true);
      }
    }, [error, file, isDefaultImage]);

    useEffect(() => {
      // Update preview if defaultImage changes
      if (defaultImage && !file && !preview) {
        setPreview(defaultImage);
        setIsDefaultImage(true);
      }
    }, [defaultImage, file, preview]);

    const localOnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
          setFile(selectedFile);
          if (isImageFile(selectedFile)) {
            const objectUrl = URL.createObjectURL(selectedFile);
            setPreview(objectUrl);
          }
          onChange(selectedFile);
          setLocalError(false);
          setIsDefaultImage(false);
        } else {
          setFile(null);
          setPreview(defaultImage || null);
          setIsDefaultImage(!!defaultImage);
          onChange();
        }
      },
      [onChange, defaultImage],
    );

    const localRemoveImage = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setFile(null);
        setPreview(defaultImage || null);
        setIsDefaultImage(false);
        removeImage();
      },
      [removeImage, defaultImage],
    );

    useEffect(() => {
      return () => {
        if (preview && !isDefaultImage) {
          URL.revokeObjectURL(preview);
        }
      };
    }, [preview, isDefaultImage]);

    return (
      <>
        <label
          htmlFor={name}
          className={`relative flex flex-col items-center bg-white border ${
            localError ? 'border-red-500' : 'border-[#D3D2CE]'
          } rounded-2xl h-[200px] p-0 cursor-pointer justify-center gap-2 ${className ?? ''}`}
        >
          {file || (isDefaultImage && preview) ? (
            <>
              {isDefaultImage || (file && isImageFile(file)) ? (
                <img src={preview!} className="rounded-2xl max-h-full w-full object-contain" alt="Preview" />
              ) : (
                <div className="flex flex-col items-center gap-2 max-w-full px-4">
                  <FileText className="w-16 h-16 text-grey flex-shrink-0" />
                  <Text variant="text6" className="w-full truncate text-center">
                    {file?.name}
                  </Text>
                </div>
              )}
              <button
                className="absolute right-[10%] top-[10%] flex flex-col items-center gap-3"
                onClick={localRemoveImage}
              >
                <Trash2 className="w-6 h-6 p-0.5 rounded-full text-red-500" />
              </button>
            </>
          ) : (
            <>
              <Camera className="text-grey" />
              <Text variant="text6">{label}</Text>
              <Text variant="text7" className="hidden md:block">
                Supported: JPEG, PNG, PDF
              </Text>
              {localError && (
                <Text variant="text7" color="error">
                  {error}
                </Text>
              )}
            </>
          )}
          <input
            id={name}
            name={name}
            type="file"
            onChange={localOnChange}
            className="hidden"
            accept=".jpg,.jpeg,.png,.pdf"
            ref={ref}
          />
        </label>
      </>
    );
  },
);

export default UploadTag;
